import { useState, useEffect, useRef } from "react";
import { Loader2, Film, ImageIcon, X, Camera, LinkIcon, Video, ExternalLink, Play } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { uploadReferenceVideo } from "@/lib/uploadVideo";
import { toast } from "sonner";

const MODES = [
  { v: "photo", l: "Photo", icon: ImageIcon },
  { v: "video", l: "Video", icon: Video },
  { v: "link", l: "Link", icon: LinkIcon },
];

// Grab a poster frame from a local video File.
const capturePoster = (file) => new Promise((resolve) => {
  const v = document.createElement("video");
  v.preload = "metadata"; v.muted = true; v.playsInline = true;
  v.src = URL.createObjectURL(file);
  const done = (val) => { try { URL.revokeObjectURL(v.src); } catch {} resolve(val); };
  v.onloadeddata = () => { try { v.currentTime = Math.min(0.1, (v.duration || 1) * 0.1); } catch { done(null); } };
  v.onseeked = () => {
    try {
      const c = document.createElement("canvas");
      c.width = v.videoWidth || 720; c.height = v.videoHeight || 1280;
      c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
      done(c.toDataURL("image/jpeg", 0.7));
    } catch { done(null); }
  };
  v.onerror = () => done(null);
});

export default function PostComposer({ onClose, onPosted }) {
  const [mode, setMode] = useState("photo");
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [vidPct, setVidPct] = useState(0);
  const [shareToFeed, setShareToFeed] = useState(true);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkPreview, setLinkPreview] = useState(null);
  const [linkState, setLinkState] = useState("idle"); // idle | loading | ok | fail
  const vidPreviewRef = useRef(null);

  useEffect(() => {
    Promise.all([api.get("/images"), api.get("/videos")]).then(([im, vd]) => {
      const imgs = (im.data || []).filter((i) => i.image_base64).map((i) => ({ type: "image", url: `data:image/png;base64,${i.image_base64}`, media_url: null, id: i.id, image_base64: i.image_base64 }));
      const vids = (vd.data || []).filter((v) => v.status === "completed" && v.video_url).map((v) => ({ type: "video", url: v.video_url, media_url: v.video_url, id: v.id }));
      setItems([...vids, ...imgs]);
    }).catch(() => setItems([]));
  }, []);
  useEffect(() => { const h = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);

  // Switching modes clears the current media selection.
  const switchMode = (m) => { setMode(m); setSel(null); };

  // Debounced link preview.
  useEffect(() => {
    if (mode !== "link") return;
    const u = linkUrl.trim();
    if (!u || u.length < 4) { setLinkPreview(null); setLinkState("idle"); return; }
    setLinkState("loading"); setLinkPreview(null);
    const t = setTimeout(async () => {
      try { const { data } = await api.post("/link/preview", { url: u }); setLinkPreview(data); setLinkState("ok"); }
      catch { setLinkPreview(null); setLinkState("fail"); }
    }, 700);
    return () => clearTimeout(t);
  }, [linkUrl, mode]);

  const uploadImageRoll = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
      const { data } = await api.post("/uploads/image", { image_base64: b64, origin_url: window.location.origin });
      const item = { type: "image", url: data.url, media_url: data.url, id: `upload-${Date.now()}`, uploaded: true, image_base64: b64 };
      setItems((x) => [item, ...(x || [])]);
      setSel(item);
    } catch { toast.error("Upload failed"); } finally { setUploading(false); e.target.value = ""; }
  };

  const chooseVideoFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) { return; }
    if (!f.type.startsWith("video/")) { toast.error("Please choose a video file"); e.target.value = ""; return; }
    if (f.size > 200 * 1024 * 1024) { toast.error("Video is too large (max 200MB)"); e.target.value = ""; return; }
    const previewUrl = URL.createObjectURL(f);
    setSel({ type: "video", upload: true, file: f, media_url: null, previewUrl, posterData: null });
    const poster = await capturePoster(f);
    setSel((s) => (s && s.file === f ? { ...s, posterData: poster } : s));
    e.target.value = "";
  };

  const post = async () => {
    setBusy(true);
    try {
      if (mode === "link") {
        if (linkState !== "ok" || !linkPreview) { setBusy(false); return; }
        await api.post("/posts", { media_type: "link", link: linkPreview, caption, to_feed: shareToFeed });
      } else {
        if (!sel) { setBusy(false); return; }
        let media_url = sel.media_url;
        let thumb_url = sel.media_url;
        if (sel.type === "image" && !sel.uploaded) {
          const { data } = await api.post("/uploads/image", { image_base64: sel.image_base64, origin_url: window.location.origin });
          media_url = data.url; thumb_url = data.url;
        } else if (sel.type === "video" && sel.upload) {
          if (sel.posterData) {
            try { const { data } = await api.post("/uploads/image", { image_base64: sel.posterData, origin_url: window.location.origin }); thumb_url = data.url; }
            catch { thumb_url = null; }
          } else thumb_url = null;
          setVidPct(1);
          const data = await uploadReferenceVideo(sel.file, setVidPct);
          media_url = data.url;
        }
        await api.post("/posts", { media_url, media_type: sel.type, thumb_url, caption, to_feed: shareToFeed });
      }
      toast.success(shareToFeed ? "Posted to your feed 🎉" : "Posted to your profile");
      onPosted();
    } catch (e) {
      if (e.response?.status === 402) toast.error(formatApiError(e.response?.data?.detail) || "Not enough coins.");
      else toast.error(formatApiError(e.response?.data?.detail) || "Couldn't post");
      setBusy(false); setVidPct(0);
    }
  };

  const canPost = mode === "link" ? linkState === "ok" && !!linkPreview : !!sel;
  const gridItems = (items || []).filter((it) => (mode === "video" ? it.type === "video" : it.type === "image"));

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="post-composer">
      <div className="absolute inset-0 bg-black/75" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-ink-900 border-t sm:border border-zinc-800 sm:rounded-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <span className="font-heading font-bold">New Post</span>
          <button onClick={onClose} data-testid="post-composer-close" className="text-zinc-400"><X size={20} /></button>
        </div>

        {/* mode tabs */}
        <div className="flex items-center gap-1 px-4 pt-3">
          {MODES.map((m) => (
            <button key={m.v} onClick={() => switchMode(m.v)} data-testid={`composer-mode-${m.v}`}
              className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-heading font-bold border transition-colors ${mode === m.v ? "border-gold text-gold bg-[#1a140d]" : "border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}>
              <m.icon size={15} /> {m.l}
            </button>
          ))}
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          <textarea value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="post-caption" rows={2} placeholder="Add a description…"
            className="w-full bg-ink-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none resize-none" />

          {/* PHOTO */}
          {mode === "photo" && (
            <>
              <label data-testid="post-upload-roll" className="flex items-center justify-center gap-2 border border-dashed border-[#22c55e]/60 text-[#22c55e] rounded-xl py-3 text-sm font-heading font-bold cursor-pointer hover:bg-[#0f2016] transition-colors">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />} Upload a photo
                <input type="file" accept="image/*" onChange={uploadImageRoll} className="hidden" />
              </label>
              <CreationsGrid items={items} gridItems={gridItems} sel={sel} setSel={setSel} empty="No photos yet — upload one above or make something with Create." />
            </>
          )}

          {/* VIDEO */}
          {mode === "video" && (
            <>
              {sel?.upload ? (
                <div className="relative rounded-xl overflow-hidden border border-zinc-800 bg-black" data-testid="composer-video-preview">
                  <video ref={vidPreviewRef} src={sel.previewUrl} controls playsInline className="w-full max-h-64 object-contain bg-black" />
                  <button onClick={() => setSel(null)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white"><X size={16} /></button>
                </div>
              ) : (
                <label data-testid="post-upload-video" className="flex items-center justify-center gap-2 border border-dashed border-[#22c55e]/60 text-[#22c55e] rounded-xl py-3 text-sm font-heading font-bold cursor-pointer hover:bg-[#0f2016] transition-colors">
                  <Video size={16} /> Upload or record a video
                  <input type="file" accept="video/*" onChange={chooseVideoFile} className="hidden" />
                </label>
              )}
              {busy && vidPct > 0 && (
                <div className="w-full h-2 rounded-full bg-zinc-800 overflow-hidden" data-testid="composer-video-progress">
                  <div className="h-full bg-gold transition-all" style={{ width: `${vidPct}%` }} />
                </div>
              )}
              <CreationsGrid items={items} gridItems={gridItems} sel={sel} setSel={setSel} empty="No videos yet — upload one above or animate something with Create." />
            </>
          )}

          {/* LINK */}
          {mode === "link" && (
            <>
              <div className="flex items-center gap-2 bg-ink-950 border border-zinc-800 rounded-lg px-3 py-2 focus-within:border-gold">
                <LinkIcon size={15} className="text-zinc-500 shrink-0" />
                <input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} data-testid="post-link-input"
                  placeholder="Paste a YouTube, Vimeo, or website link…" className="flex-1 bg-transparent text-sm focus:outline-none" />
                {linkState === "loading" && <Loader2 size={15} className="animate-spin text-gold shrink-0" />}
              </div>
              {linkState === "fail" && (
                <div className="text-xs text-zinc-500 border border-zinc-800 rounded-lg px-3 py-3" data-testid="post-link-fail">
                  Preview unavailable — the post will still link to this URL.
                </div>
              )}
              {linkState === "ok" && linkPreview && (
                <div className="rounded-xl overflow-hidden border border-zinc-800 bg-ink-950" data-testid="post-link-preview">
                  {linkPreview.image ? (
                    <div className="relative w-full aspect-video bg-black">
                      <img src={linkPreview.image} alt="" className="w-full h-full object-cover" />
                      {linkPreview.kind === "video" && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-black/55 border border-white/30 flex items-center justify-center"><Play size={20} className="text-white translate-x-0.5" fill="white" /></div>
                        </div>
                      )}
                    </div>
                  ) : null}
                  <div className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-gold/80 mb-1"><ExternalLink size={11} /> {linkPreview.site_name}</div>
                    <div className="font-heading font-bold text-sm leading-snug line-clamp-2">{linkPreview.title}</div>
                    {linkPreview.description && <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{linkPreview.description}</p>}
                    {linkPreview.playable && <p className="text-[11px] text-[#22c55e] mt-1.5">▶ Plays right in the feed</p>}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-3 border-t border-white/10 space-y-3">
          <div className="flex items-center justify-between">
            <div><div className="text-sm font-heading font-semibold">Share to feed</div><div className="text-[11px] text-zinc-500">{shareToFeed ? "Shows on the public feed · costs 1 coin" : "Only on your profile · free"}</div></div>
            <button onClick={() => setShareToFeed((v) => !v)} data-testid="composer-feed-toggle" className={`w-11 h-6 rounded-full flex items-center transition-colors shrink-0 ${shareToFeed ? "" : "bg-zinc-700"}`} style={shareToFeed ? { backgroundColor: "#22e06b" } : {}}>
              <span className={`w-5 h-5 bg-white rounded-full transition-transform ${shareToFeed ? "translate-x-5" : "translate-x-0.5"}`} /></button>
          </div>
          <button onClick={post} disabled={!canPost || busy} data-testid="post-submit" className="w-full flex items-center justify-center gap-2 rounded-lg bg-gold text-ink-950 font-heading font-black py-3 disabled:opacity-50">
            {busy ? <Loader2 size={18} className="animate-spin" /> : shareToFeed ? <>Post to Feed · 1 coin</> : <>Post to Profile</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreationsGrid({ items, gridItems, sel, setSel, empty }) {
  return (
    <>
      <p className="text-xs text-zinc-500">Or pick one of your creations:</p>
      {items === null ? <Loader2 className="animate-spin text-gold mx-auto" size={22} />
        : gridItems.length === 0 ? <p className="text-center text-sm text-zinc-600 py-6">{empty}</p>
        : <div className="grid grid-cols-3 gap-2" data-testid="composer-grid">
            {gridItems.map((it) => (
              <button key={`${it.type}-${it.id}`} onClick={() => setSel(it)} data-testid={`composer-item-${it.id}`}
                className={`relative aspect-square rounded-lg overflow-hidden border-2 ${sel?.id === it.id ? "border-gold" : "border-transparent"}`}>
                {it.type === "video" ? <video src={it.url} muted className="w-full h-full object-cover" /> : <img src={it.url} className="w-full h-full object-cover" alt="" />}
                <span className="absolute top-1 left-1 bg-black/60 rounded px-1 text-[9px]">{it.type === "video" ? <Film size={9} /> : <ImageIcon size={9} />}</span>
              </button>
            ))}
          </div>}
    </>
  );
}
